/**
 * Web push for the installed widget (FEATURE_WIDGET_PWA_WEB_PUSH.md §5.2).
 *
 * One service worker serves the whole origin, so every academy installed from
 * widget.apointa.com on one browser shares ONE browser push subscription. The
 * server keeps a row per club for it; logging out removes this club's row and
 * never the browser subscription, which another academy may still be using.
 *
 * Phones only. A phone browser is first offered the app for the Home Screen; the
 * notifications ask comes inside the installed app (iOS delivers push nowhere
 * else). On Android Chrome the one "Install app" tap runs both: the install dialog,
 * then the permission prompt. The browser prompt is only ever raised from a tap
 * (turnOnPush) — never on load.
 */

import api from "/imports/api/client";
import { track } from "/imports/libs/analytics";

const SNOOZE_KEY = "widget_v2_push_snoozed";
const BOUND_KEY = "widget_v2_push_bound";
const INSTALLED_KEY = "widget_v2_installed";
const INSTALL_DISMISSED_KEY = "widget_v2_install_dismissed";
const CHANGE_EVENT = "widget-push-changed";

// The card can sit on two mounted pages (home band, dashboard): a change made
// through one is announced so the other re-reads its state.
function announceChange() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Subscribe to install / on-off changes made anywhere on the page. Returns the unsubscribe. */
export function onPushChange(handler) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
const SW_READY_TIMEOUT_MS = 10000;

// localStorage / sessionStorage can be missing or throw (private mode, blocked
// site data, previews): every read falls back to "nothing stored".
function readJson(storage, key, fallback) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_err) {
    return fallback;
  }
}

function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (_err) {
    // Not remembering is harmless: the card just shows again.
  }
}

function withTimeout(promise, ms) {
  return Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error("Timed out")), ms))]);
}

export function isPushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return !!window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

// iPadOS reports itself as a Mac; a touch screen gives it away.
export function isIos(ua, maxTouchPoints = 0) {
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
}

// Web push reached iOS in 16.4, and only for web apps added to the Home Screen.
// A UA without an iOS version (iPadOS desktop mode) is a current iPad.
export function iosSupportsWebPush(ua) {
  const match = ua.match(/(?:iPhone|CPU) OS (\d+)_(\d+)/);
  if (!match) return true;
  const [major, minor] = [Number(match[1]), Number(match[2])];
  return major > 16 || (major === 16 && minor >= 4);
}

export function detectPlatform(ua, { standalone = false, maxTouchPoints = 0 } = {}) {
  if (isIos(ua, maxTouchPoints)) return standalone ? "ios-standalone" : "other";
  if (/Android/i.test(ua)) return "android";
  if (/Windows|Macintosh|Linux|CrOS/.test(ua)) return "desktop";
  return "other";
}

// The whole install-and-notify flow is for phones and tablets: a desktop browser
// is never asked to install the app or to turn notifications on.
export function isMobileDevice(ua, maxTouchPoints = 0) {
  return isIos(ua, maxTouchPoints) || /Android/i.test(ua);
}

/**
 * Which face the install card shows. Pure, so the rules are testable:
 *   install — a phone browser: the app is not on the Home Screen (or the browser
 *             says it can be installed again, after a removal)
 *   ask     — inside the installed app, and this club doesn't have this phone yet
 *   denied  — the browser blocks notifications; only the phone's settings can undo it
 *   hidden  — a desktop, already installed or on, unsupported, or snoozed
 */
export function pickCardState({
  mobile,
  standalone,
  installed = false,
  installable = false,
  installDismissed = false,
  pushEnabled = true,
  supported,
  permission,
  boundHere,
  snoozed
}) {
  if (!mobile) return "hidden";
  if (!standalone) {
    if (installDismissed) return "hidden";
    // Chrome only offers an install for an app the phone doesn't have: after an
    // uninstall the offer comes back, and so does the strip.
    if (installable) return "install";
    return installed ? "hidden" : "install";
  }
  if (!pushEnabled || !supported || snoozed) return "hidden";
  if (permission === "granted" && boundHere) return "hidden";
  if (permission === "denied") return "denied";
  return "ask";
}

export function currentCardState(slug, { pushEnabled = true } = {}) {
  const ua = navigator.userAgent || "";
  const supported = isPushSupported();
  return pickCardState({
    mobile: isMobileDevice(ua, navigator.maxTouchPoints || 0),
    standalone: isStandalone(),
    installed: installedThisVisit || isInstalled(slug),
    installable: canPromptInstall(),
    installDismissed: isInstallDismissed(slug),
    pushEnabled,
    supported,
    permission: supported ? Notification.permission : "unsupported",
    boundHere: isBoundHere(slug),
    snoozed: isSnoozed(slug)
  });
}

// "Not now" on the notifications ask: two weeks of quiet, per club.
export function isSnoozed(slug, now = Date.now()) {
  const until = readJson(localStorage, SNOOZE_KEY, {})[slug];
  return typeof until === "number" && until > now;
}

export function snooze(slug, now = Date.now()) {
  writeJson(localStorage, SNOOZE_KEY, { ...readJson(localStorage, SNOOZE_KEY, {}), [slug]: now + SNOOZE_MS });
}

function addSlug(storage, key, slug) {
  const slugs = readJson(storage, key, []);
  if (!slugs.includes(slug)) writeJson(storage, key, [...slugs, slug]);
}

// "Never show again" on the install card: this club stops asking on this browser.
export function isInstallDismissed(slug) {
  return readJson(localStorage, INSTALL_DISMISSED_KEY, []).includes(slug);
}

export function dismissInstall(slug) {
  addSlug(localStorage, INSTALL_DISMISSED_KEY, slug);
  announceChange();
}

// Clubs whose app is on the Home Screen. Set when the install dialog is accepted
// and whenever the installed app opens: on Android the app shares the browser's
// storage, so the browser stops offering an install the phone already has.
export function isInstalled(slug) {
  return readJson(localStorage, INSTALLED_KEY, []).includes(slug);
}

export function markInstalled(slug) {
  if (!slug || isInstalled(slug)) return;
  addSlug(localStorage, INSTALLED_KEY, slug);
  announceChange();
}

/**
 * Chrome on Android offers a one-tap install through `beforeinstallprompt`, which
 * fires early — before any card is mounted — so the event is caught once at start-up
 * and kept for the card's "Install app" button. Everywhere else the button shows the
 * phone's own steps instead.
 */
let deferredInstallPrompt = null;
// Installed during this visit, through the strip or the browser's own menu.
let installedThisVisit = false;
// Whether the install that just happened came from the strip's own dialog.
let promptAccepted = false;

export function captureInstallPrompt() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (event) => {
    // Claims the offer, which keeps Chrome's own install banner off the page:
    // the strip is the one place the app is offered, and it is not shown on
    // every open the way the banner is.
    event.preventDefault();
    deferredInstallPrompt = event;
    announceChange();
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    installedThisVisit = true;
    // The one install signal Chrome gives, whichever way it was started.
    track("app_installed", { method: promptAccepted ? "strip" : "browser_menu" });
    announceChange();
  });
}

// What to do by hand when there is no install dialog to open.
export function installSteps(ua, maxTouchPoints = 0) {
  return isIos(ua, maxTouchPoints)
    ? "Tap the Share button, then “Add to Home Screen”. Open the app from there."
    : "Open your browser menu, then tap “Install app” or “Add to Home screen”.";
}

export function canPromptInstall() {
  return deferredInstallPrompt !== null;
}

/** @returns {Promise<"accepted"|"dismissed"|"unavailable">} */
export async function promptInstall() {
  const event = deferredInstallPrompt;
  if (!event) return "unavailable";
  // A prompt event can only be used once; a refusal falls back to the steps.
  deferredInstallPrompt = null;
  event.prompt();
  const { outcome } = await event.userChoice;
  promptAccepted = outcome === "accepted";
  announceChange();
  return outcome;
}

// Clubs this device has been handed to. Kept through logout on purpose: the next
// sign-in at the same club rebinds the phone to whoever signed in (D7).
function isBoundHere(slug) {
  return readJson(localStorage, BOUND_KEY, []).includes(slug);
}

function markBound(slug) {
  addSlug(localStorage, BOUND_KEY, slug);
}

export function urlBase64ToUint8Array(base64) {
  const padded = `${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`.replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function sameKey(current, expected) {
  // Some browsers don't expose the key the subscription was made with.
  if (!current) return true;
  const bytes = new Uint8Array(current);
  return bytes.length === expected.length && bytes.every((b, i) => b === expected[i]);
}

async function existingSubscription() {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  return registration ? registration.pushManager.getSubscription() : null;
}

async function bindDevice({ slug, publicKey }) {
  // subscribe() needs an active worker; on a first visit it may still be installing.
  const registration = await withTimeout(navigator.serviceWorker.ready, SW_READY_TIMEOUT_MS);
  const key = urlBase64ToUint8Array(publicKey);

  let subscription = await registration.pushManager.getSubscription();
  // A subscription made with another server key can't be sent to: replace it.
  if (subscription && !sameKey(subscription.options?.applicationServerKey, key)) {
    await subscription.unsubscribe().catch(() => {});
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  }

  const platform = detectPlatform(navigator.userAgent || "", { standalone: isStandalone(), maxTouchPoints: navigator.maxTouchPoints || 0 });
  await api.pushSubscribe(slug, subscription.toJSON(), platform);
  markBound(slug);
  announceChange();
}

/**
 * "Turn on" from the prompt card. Must run straight from the tap: Safari only
 * honours requestPermission() inside the user gesture, so nothing is awaited first.
 *
 * @returns {Promise<{ permission: NotificationPermission }>}
 */
export async function turnOnPush({ slug, publicKey }) {
  const permission = await Notification.requestPermission();
  if (permission === "granted") await bindDevice({ slug, publicKey });
  return { permission };
}

/**
 * On every open while signed in: keep the server's row in step with this phone.
 * Only for a club this phone was turned on for — another club on the same
 * origin still asks through its own card. Rebinds after a sign-in on a shared
 * phone, and re-creates a subscription the browser dropped or rotated.
 */
export async function syncDevice({ slug, publicKey }) {
  if (!isPushSupported() || Notification.permission !== "granted" || !isBoundHere(slug)) return;

  const subscription = await existingSubscription();
  if (subscription && sameKey(subscription.options?.applicationServerKey, urlBase64ToUint8Array(publicKey))) {
    const { known } = await api.pushTouch(slug, subscription.endpoint);
    if (known) return;
  }
  await bindDevice({ slug, publicKey });
}

/**
 * Logout: take this phone off the account at this club (D7), so the next person
 * signing in on a shared phone never gets the previous client's notifications.
 * Best-effort and bounded — a slow network must not hold the logout up.
 */
export async function unbindDevice(slug, { timeoutMs = 1500 } = {}) {
  if (!slug) return;
  const work = (async () => {
    const subscription = await existingSubscription();
    if (subscription) await api.pushUnsubscribe(slug, subscription.endpoint);
  })();
  await withTimeout(work, timeoutMs);
}
